/**
 * Safe post-auth redirects: relative paths (same site) or absolute URLs on allowlisted marketplace origins.
 */

import {
	getConfiguredFrontendOrigin,
	primaryFrontendOrigin,
} from "@/lib/utils/frontend-origin";

function originsFromFrontendEnv(): string[] {
  const raw = process.env.NEXT_PUBLIC_FRONTEND_URI;
  if (!raw) return [];
  const out = new Set<string>();
  for (const segment of raw.split(",").map((s) => s.trim()).filter(Boolean)) {
    const origin = primaryFrontendOrigin(segment);
    if (origin) out.add(origin.toLowerCase());
  }
  const primary = getConfiguredFrontendOrigin();
  if (primary) out.add(primary.toLowerCase());
  return [...out];
}

/** Origins allowed for absolute returnUrl (open-redirect protection). */
export function getAllowedExternalRedirectOrigins(): string[] {
  const set = new Set<string>([
    ...originsFromFrontendEnv(),
    "https://techxeed.com",
    "https://www.techxeed.com",
  ]);
  if (process.env.NODE_ENV === "development") {
    set.add("http://localhost:3000");
    set.add("http://127.0.0.1:3000");
  }
  return [...set];
}

export function getSafeRedirectUrl(returnUrl: string | null | undefined): string {
  const u = (returnUrl ?? "").trim();
  if (!u) return "/";
  if (u.startsWith("//")) return "/";
  if (u.startsWith("http")) return validateReturnUrl(u) ? u : "/";
  if (u.startsWith("/")) return u;
  return "/";
}

export function getSafePostAuthRedirectUrl(returnUrl: string | null | undefined): string {
  const safeUrl = getSafeRedirectUrl(returnUrl);

  try {
    const url = safeUrl.startsWith("http")
      ? new URL(safeUrl)
      : new URL(safeUrl, "http://localhost");
    const path = url.pathname.replace(/\/+$/, "") || "/";

    if (path === "/login" || path.startsWith("/login/") || path === "/signup" || path.startsWith("/signup/")) {
      return "/";
    }
  } catch {
    return "/";
  }

  return safeUrl;
}

/**
 * Validate absolute returnUrl to prevent open redirects.
 */
export function validateReturnUrl(returnUrl: string): boolean {
  try {
    const url = new URL(returnUrl);
    if (url.protocol !== "http:" && url.protocol !== "https:") return false;

    const origin = url.origin.toLowerCase();
    const allowed = getAllowedExternalRedirectOrigins();
    if (!allowed.includes(origin)) {
      console.error("[Security] Invalid returnUrl origin:", url.origin);
      return false;
    }

    if (process.env.NODE_ENV === "production" && url.protocol !== "https:") {
      console.error("[Security] Non-HTTPS returnUrl in production:", returnUrl);
      return false;
    }

    return true;
  } catch (error) {
    console.error("[Security] Invalid returnUrl format:", returnUrl, error);
    return false;
  }
}
